import React from 'react';
import PropTypes from 'prop-types';
import { Link, Typography, applyTextStyles, clickProps, selectSystemProps, useThemeTokens } from '@telus-uds/components-base';
import styled from 'styled-components';
import { htmlAttrs } from '../../utils';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
const [selectProps, selectedSystemPropTypes] = selectSystemProps([htmlAttrs]);
const StyledItemContainer = /*#__PURE__*/styled.li.withConfig({
  displayName: "Item__StyledItemContainer",
  componentId: "components-web__sc-1rfdaul-0"
})({
  display: 'inline-block',
  paddingLeft: _ref => {
    let {
      listItemPadding
    } = _ref;
    return `${listItemPadding}px`;
  },
  lineHeight: _ref2 => {
    let {
      lineHeight,
      fontSize
    } = _ref2;
    return `${Math.ceil(lineHeight * fontSize)}px`;
  },
  marginBottom: '8px'
});
const IconContainer = /*#__PURE__*/styled.span.withConfig({
  displayName: "Item__IconContainer",
  componentId: "components-web__sc-1rfdaul-1"
})({
  display: 'inline-block',
  paddingLeft: _ref3 => {
    let {
      iconPadding
    } = _ref3;
    return `${iconPadding}px`;
  },
  paddingRight: _ref4 => {
    let {
      iconPadding
    } = _ref4;
    return `${iconPadding}px`;
  },
  fontFamily: _ref5 => {
    let {
      fontName,
      fontWeight
    } = _ref5;
    return applyTextStyles({
      fontName,
      fontWeight
    }).fontFamily;
  },
  height: _ref6 => {
    let {
      iconContainerSize
    } = _ref6;
    return `${iconContainerSize}px`;
  },
  width: _ref7 => {
    let {
      iconContainerSize,
      iconPadding
    } = _ref7;
    return `${iconContainerSize + iconPadding * 2}px`;
  },
  verticalAlign: 'bottom'
});
const Item = /*#__PURE__*/React.forwardRef((_ref8, ref) => {
  let {
    href,
    children,
    current = false,
    tokens,
    variant = {
      light: true
    },
    // `light` variant (shared with the `Link` component) is default by design
    LinkRouter,
    linkRouterProps,
    onPress,
    ...rest
  } = _ref8;
  const {
    iconColor,
    icon: ChevronRightIcon,
    currentColor,
    color,
    iconSize,
    fontSize,
    fontWeight,
    fontName,
    lineHeight,
    listItemPadding,
    iconContainerSize,
    iconPadding
  } = useThemeTokens('Breadcrumbs', tokens, variant);
  const linkOptions = {
    ...clickProps.toPressProps(selectProps(rest)),
    href
  };
  return /*#__PURE__*/_jsx(StyledItemContainer, {
    listItemPadding: listItemPadding,
    lineHeight: lineHeight,
    fontSize: fontSize,
    iconContainerSize: iconContainerSize,
    children: current ? /*#__PURE__*/_jsx(Typography, {
      tokens: {
        color: currentColor,
        fontSize,
        fontWeight,
        lineHeight
      },
      variant: variant,
      children: children
    }) : /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(Link, {
        ...linkOptions,
        ref: ref,
        tokens: {
          color,
          blockFontSize: fontSize,
          blockLineHeight: lineHeight
        },
        LinkRouter: LinkRouter,
        linkRouterProps: linkRouterProps,
        variant: variant,
        onPress: onPress,
        children: children
      }), /*#__PURE__*/_jsx(IconContainer, {
        iconPadding: iconPadding,
        fontName: fontName,
        fontWeight: fontWeight,
        iconContainerSize: iconContainerSize,
        children: /*#__PURE__*/_jsx(ChevronRightIcon, {
          size: iconSize,
          color: iconColor
        })
      })]
    })
  });
});
Item.displayName = 'Item';
Item.propTypes = {
  ...selectedSystemPropTypes,
  /**
   * Breadcrumb text
   */
  children: PropTypes.node.isRequired,
  /**
   * @ignore
   *
   * Indicates whether or not the Item should be as current/active
   */
  current: PropTypes.bool,
  /**
   * Target URL. This will be converted to `to` if the `LinkRouter`
   * prop is provided to the `Item` or parent `Breadcrumbs` element.
   */
  href: PropTypes.string.isRequired,
  /**
   * Variant to render.
   */
  variant: PropTypes.shape({
    inverse: PropTypes.bool
  }),
  /**
   * Function to be called when the Item is clicked.
   */
  onPress: PropTypes.func
};
export default Item;